import { Injectable, computed, inject, signal } from '@angular/core';
import { ENROLL_EVENT_REPORT } from 'src/app/@constants/local-storage-key-constants';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { getLocalStorageItem } from 'src/app/@utils/local-storage-util';
import { resolveMatchmakingEventIdFromState } from 'src/app/@utils/matchmaking/resolve-matchmaking-event-id';

/** The signed-in user session. Mirrors the React `src/lib/session.js` shape. */
export interface Session {
  userId: string;
  role: 'attendee' | 'exhibitor';
  expoId: string;
  name?: string;
  enriched?: boolean;
  /** Set after login so callers know which experience to load. */
  mode?: 'event' | 'test';
}

const KEY = 'synopsis.session';

const _session = signal<Session | null>(read());

@Injectable({ providedIn: 'root' })
export class SessionService {
  public readonly session = _session.asReadonly();
  public readonly userId = computed(() => _session()?.userId ?? null);
  public readonly role = computed(() => _session()?.role ?? null);
  public readonly isAuthed = computed(() => !!_session()?.userId);

  private readonly _eventConfigState = inject(EventConfigStateService);

  set(session: Session): void {
    _session.set(session);
    try {
      localStorage.setItem(KEY, JSON.stringify(session));
    } catch {
      /* storage unavailable — keep in-memory */
    }
  }

  clear(): void {
    _session.set(null);
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }

  /** Re-seed the show-winners session from `ENROLL_EVENT_REPORT` when it exists.
   *  Called on app boot, before networking screens load, and from the auth
   *  interceptor when headers would otherwise be empty. Returns the email. */
  syncFromEnrollReport(): string | null {
    const enroll = getLocalStorageItem<{
      email?: string;
      eventNameIdentifier?: string;
    }>(ENROLL_EVENT_REPORT);
    const email = enroll?.email?.toLowerCase().trim();
    if (!email) return null;

    const expoId = resolveMatchmakingEventIdFromState(
      this._eventConfigState,
      undefined,
      enroll?.eventNameIdentifier
    );
    const current = _session();
    if (!current || current.userId !== email || current.expoId !== expoId) {
      this.set({
        userId: email,
        role: 'attendee',
        expoId,
        name: email.split('@')[0],
        enriched: current?.enriched ?? false,
        mode: current?.mode ?? 'event',
      });
    }
    return email;
  }
}

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}
